import InputTypesEnum from '../../../Enums/InputTypesEnum';
import ValidityHelper from '../../../Helpers/ValidityHelper';
import moneyHelper from '../../../Helpers/moneyHelper';
import dateTimeHelper from '../../../Helpers/datetimeHelper';
import FeeTypesEnum from '../../../Enums/FeeTypesEnum';
import FreelancerFeeItem from '../../../Domain/FreelancerFeeItem';
import FreelancerFeeService from '../../../Services/FreelancerFeeService';

// Trivial placeholder: the wizard only instantiates windowDescription.itemType when no
// editedItem prop is passed. FreelancerFeeAdmin always passes a pre-fetched editedItem
// (crudType=Create, built in FreelancerFeeAdmin.applyFeeForVendorFunction after the vendor-list
// prefetch resolves), so this is never invoked, but it keeps the windowDescription contract
// satisfied.
class ApplyFeeForVendorsEditItem {}

export default class ApplyFeeForVendorsWindowDescription {
  static itemType = ApplyFeeForVendorsEditItem;
  static finishButtonLabel = 'Apply Fee for the Selected Vendors';
  static messageOnSuccess = 'Fee has been applied for the selected vendors.';

  // Mirrors the old ApplyFeeForVendorsWindow's OverWindow title verbatim.
  static titleForCreate = (item) =>
    `Apply Fee for Vendors ${
      item ? `(${item.FreelancerUserName}, ${item.ClientName === '-' ? '' : `${item.ClientName}, `}FeeId: ${item.FreelancerFeeId})` : ''
    }`;

  // Keeps AvailableVendors[].SelectedInList in sync with the MultiChoice selection (loose String
  // compare, same idiom as AddSurveyWindowDescription.syncSelectedRows). Synchronous only -
  // onFieldChange's return value is written straight into editedItem and must never be a Promise
  // (see GeneralEditWizzardPageFigma.updateRelatedData).
  static syncSelectedVendors(item) {
    const selectedIds = item.SelectedVendorIds ?? [];
    item.AvailableVendors.forEach((vendor) => {
      vendor.SelectedInList = selectedIds.some((id) => String(id) === String(vendor.FreelancerUserId));
    });
  }

  // Mirrors the old panel's renderUseOptFee onChange: enabling starts the opt fee at 0, disabling
  // marks it unused (-1) so it is excluded from the apply.
  static onUseOptFeeChange(item) {
    item.OptFee = item.UseOptFee ? 0 : -1;
  }

  static isDataQuality = (item) => item.FeeType === FeeTypesEnum.DataQuality;

  // Mirrors the old panel's handleOnSubmit payload exactly: the (possibly edited) Fee/OptFee/Tier,
  // the source FreelancerFeeId, and the ids of the vendors moved into the destination list.
  // FreelancerFeeService.ApplyFeeForEmployedFreelancersPostAsync stringifies Fee/OptFee/FreelancerFeeId.
  // MUST return the promise (wizard contract).
  static serviceFunction = (edited) =>
    FreelancerFeeService.ApplyFeeForEmployedFreelancersPostAsync({
      Fee: edited.Fee,
      OptFee: edited.OptFee,
      Tier: edited.Tier,
      FreelancerFeeId: edited.FreelancerFeeId,
      UserIds: edited.AvailableVendors.filter((vendor) => vendor.SelectedInList).map(
        (vendor) => vendor.FreelancerUserId
      ),
    });

  // Plain-text End Date (old panel's toYYYYsMMsDDWithIndefiniteIcon returns a JSX <FaIcon/> for an
  // indefinite date, which can't sit in a TextReadOnly's text value).
  static formatEndDate(item) {
    return dateTimeHelper.isIndefinite(item.EndDate) ? 'Indefinite' : dateTimeHelper.toYYYYsMMsDD(item.EndDate);
  }

  static pagesInGeneralEditWizzard = [
    {
      fields: [
        // --- Read-only "Fee Information" context, ported from the old panel's renderInfo grid ---
        {
          fieldName: 'ClientName',
          label: 'Client',
          className: 'col-3',
          type: InputTypesEnum.TextReadOnly,
          validityChecker: ValidityHelper.AlwaysFlawless,
        },
        {
          fieldName: (item) => dateTimeHelper.toYYYYsMMsDD(item.StartDate),
          label: 'Start Date',
          className: 'col-3',
          type: InputTypesEnum.TextReadOnly,
          validityChecker: ValidityHelper.AlwaysFlawless,
        },
        {
          fieldName: (item) => ApplyFeeForVendorsWindowDescription.formatEndDate(item),
          label: 'End Date',
          className: 'col-3',
          type: InputTypesEnum.TextReadOnly,
          validityChecker: ValidityHelper.AlwaysFlawless,
        },
        {
          fieldName: 'CountryName',
          label: 'Country',
          className: 'col-3',
          type: InputTypesEnum.TextReadOnly,
          validityChecker: ValidityHelper.AlwaysFlawless,
        },
        {
          // OriginalFee/OriginalOptFee/OriginalTier are frozen by the host when opening - Fee/
          // OptFee/Tier themselves are the editable fields below, so reading them here would make
          // the "Original ..." display follow the user's edits.
          fieldName: (item) => moneyHelper.toMoneyFormat(item.OriginalFee, item.FreelancerUserCurrencyCode),
          label: 'Original Fee',
          className: 'col-3',
          type: InputTypesEnum.TextReadOnly,
          validityChecker: ValidityHelper.AlwaysFlawless,
        },
        {
          fieldName: (item) => moneyHelper.toMoneyFormat(item.OriginalOptFee, item.FreelancerUserCurrencyCode),
          label: 'Original Opt Fee',
          className: 'col-3',
          type: InputTypesEnum.TextReadOnly,
          hide: (item) => parseFloat(item.OriginalOptFee) < 0,
          validityChecker: ValidityHelper.AlwaysFlawless,
        },
        {
          fieldName: 'FeeType',
          label: 'Fee Type',
          className: 'col-3',
          type: InputTypesEnum.TextReadOnly,
          validityChecker: ValidityHelper.AlwaysFlawless,
        },
        {
          fieldName: 'FreelancerFeeId',
          label: 'Fee Id',
          className: 'col-3',
          type: InputTypesEnum.TextReadOnly,
          validityChecker: ValidityHelper.AlwaysFlawless,
        },
        {
          fieldName: 'FreelancerUserCurrencyCode',
          label: 'Currency',
          className: 'col-3',
          type: InputTypesEnum.TextReadOnly,
          validityChecker: ValidityHelper.AlwaysFlawless,
        },
        {
          fieldName: 'FreelancerUserId',
          label: 'Agency Vendor Id',
          className: 'col-3',
          type: InputTypesEnum.TextReadOnly,
          validityChecker: ValidityHelper.AlwaysFlawless,
        },
        {
          fieldName: 'FreelancerUserName',
          label: 'Agency',
          className: 'col-3',
          type: InputTypesEnum.TextReadOnly,
          validityChecker: ValidityHelper.AlwaysFlawless,
        },
        {
          fieldName: (item) => (item.SelfAssigned ? 'Yes' : 'No'),
          label: 'Self Assigned',
          className: 'col-3',
          type: InputTypesEnum.TextReadOnly,
          validityChecker: ValidityHelper.AlwaysFlawless,
        },
        {
          fieldName: 'SurveyName',
          label: 'Survey',
          className: 'col-3',
          type: InputTypesEnum.TextReadOnly,
          hide: (item) => !item.SurveyName,
          validityChecker: ValidityHelper.AlwaysFlawless,
        },
        {
          fieldName: 'OriginalTier',
          label: 'Original Tier',
          className: 'col-3',
          type: InputTypesEnum.TextReadOnly,
          validityChecker: ValidityHelper.AlwaysFlawless,
        },
        {
          fieldName: 'WaveId',
          label: 'WaveId',
          className: 'col-3',
          type: InputTypesEnum.TextReadOnly,
          hide: (item) => !item.WaveId,
          validityChecker: ValidityHelper.AlwaysFlawless,
        },

        // --- Vendor transfer-list, replaced by the generic MultiChoice (verified against
        // renderers/multiChoiceRenderers.jsx + BaseInputs/Selectors/MultiChoice/MultiChoice.js) ---
        {
          fieldName: 'SelectedVendorIds',
          label: 'Vendors',
          className: 'col-12',
          type: InputTypesEnum.MultiChoice,
          selectvalues: (item) =>
            item.AvailableVendors.map((vendor) => ({ label: vendor.DisplayLabel, value: vendor.FreelancerUserId })),
          validityChecker: ValidityHelper.forRequiredMultiSelectField,
          onFieldChange: (item) => ApplyFeeForVendorsWindowDescription.syncSelectedVendors(item),
        },

        // --- Fee fields, ported 1:1 from the old panel's renderFeeFields ---
        {
          fieldName: 'Fee',
          label: (item) => `Fee (${item.FreelancerUserCurrencyCode})`,
          className: 'col-3',
          type: InputTypesEnum.NumberWithTwoDecimalPlaces,
          validityChecker: ValidityHelper.forRequiredNumberFieldAtLeastZero,
        },
        {
          fieldName: 'Tier',
          label: 'Tier',
          className: 'col-3',
          type: InputTypesEnum.Select,
          selectvalues: () => FreelancerFeeItem.getTierSelectOptions(),
          hide: (item) => !ApplyFeeForVendorsWindowDescription.isDataQuality(item),
          validityChecker: ValidityHelper.forRequiredTextField,
        },
        {
          fieldName: 'UseOptFee',
          label: 'Use Opt Fee',
          className: 'col-3',
          type: InputTypesEnum.OnOff,
          hide: (item) => !ApplyFeeForVendorsWindowDescription.isDataQuality(item),
          onFieldChange: ApplyFeeForVendorsWindowDescription.onUseOptFeeChange,
        },
        {
          fieldName: 'OptFee',
          label: (item) => `Opt Fee (${item.FreelancerUserCurrencyCode})`,
          className: 'col-3',
          type: InputTypesEnum.NumberWithTwoDecimalPlaces,
          hide: (item) => !ApplyFeeForVendorsWindowDescription.isDataQuality(item) || parseFloat(item.OptFee) < 0,
          validityChecker: ValidityHelper.forRequiredNumberFieldAtLeastZero,
        },
      ],
    },
  ];
}
